import { NameColumn } from '@/components/NameColumn';
import { getColumnWidth } from '@/components/table/columns/registry';
import type { Columns } from '@/components/table/types';
import { AssetStatusComponent } from '@/sections/asset/components/badges/AssetStatus';
import { AttackSurfaceComponent } from '@/sections/asset/components/tabs/AttackSurfaceTab';
import type { Asset } from '@/types';

/**
 * Extracts port and protocol from a web application URL
 * @param url - The URL string (group + identifier or just identifier)
 * @returns Object with port and protocol, or defaults
 */
function extractPortAndProtocol(url: string | undefined): {
  port: string;
  protocol: string;
} {
  if (!url) {
    return { port: '-', protocol: '-' };
  }

  try {
    // Handle URLs that might not have a protocol prefix
    const urlWithProtocol = url.includes('://') ? url : `https://${url}`;
    const parsed = new URL(urlWithProtocol);

    // Extract protocol (remove trailing colon)
    const protocol = parsed.protocol.replace(':', '') || '-';

    // Extract port - use explicit port or derive from protocol
    let port = parsed.port;
    if (!port) {
      if (protocol === 'https') port = '443';
      else if (protocol === 'http') port = '80';
      else port = '-';
    }

    return { port, protocol };
  } catch {
    return { port: '-', protocol: '-' };
  }
}

/**
 * Builds column definitions for WebApplication entity tables
 *
 * WebApplications are Assets with class='webapplication', typically URLs
 *
 * Returns columns for:
 * - status: Asset status
 * - identifier: Primary asset identifier (bold purple) with group below if different
 * - port: Port extracted from URL
 * - protocol: Protocol extracted from URL (http/https)
 * - surface: Attack surface classification
 *
 * @returns Column definitions for TanStack Table
 */
export function buildWebApplicationColumns(): Columns<Asset> {
  return [
    {
      id: 'status',
      label: 'Asset Status',
      ...getColumnWidth('shared', 'status'),
      cell: (item: Asset) =>
        item?.status ? <AssetStatusComponent asset={item} /> : '-',
    },
    {
      id: 'identifier',
      label: 'Identifier',
      ...getColumnWidth('asset', 'identifier'),
      cell: (item: Asset) => {
        if (!item?.identifier) {
          return '-';
        }
        // Show group as description if it differs from identifier
        const description =
          item.name && item.name !== item.identifier ? item.name : item.dns;
        return <NameColumn name={item.identifier} description={description} />;
      },
    },
    {
      id: 'port',
      label: 'Port',
      ...getColumnWidth('shared', 'port'),
      sorting: false,
      cell: (item: Asset) => {
        const { port } = extractPortAndProtocol(item?.identifier);
        return <span>{port}</span>;
      },
    },
    {
      id: 'protocol',
      label: 'Protocol',
      ...getColumnWidth('shared', 'service'),
      sorting: false,
      cell: (item: Asset) => {
        const { protocol } = extractPortAndProtocol(item?.identifier);
        return <span>{protocol}</span>;
      },
    },
    {
      id: 'attackSurface',
      label: 'Attack Surface',
      ...getColumnWidth('asset', 'attackSurface'),
      sorting: false,
      cell: (item: Asset) =>
        item?.attackSurface?.length ? (
          <AttackSurfaceComponent
            asset={item}
            maxVisible={1}
            size="xs"
            wrapInTooltip
          />
        ) : (
          '-'
        ),
    },
    {
      id: 'created',
      label: 'First Seen',
      ...getColumnWidth('shared', 'created'),
      cell: 'date' as const,
    },
    {
      id: 'visited',
      label: 'Last Seen',
      ...getColumnWidth('shared', 'visited'),
      cell: 'date' as const,
    },
  ];
}
